import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Icons, Image } from 'stylewhere/components'
import { theme } from 'stylewhere/theme'

const errorColor = '#dd4949'

interface InputProps extends Partial<HTMLInputElement> {
  onEnter?: (value: string) => void
  error?: string
  barcode?: boolean
  autoFocus?: boolean
  style?: any
  image?: any
  onChange?: (value: string) => void
  startFocus?: boolean
  label?: React.ReactNode
  debounce?: number
  showClear?: boolean
  customIcon?: React.ReactNode
  showLoading?: boolean
  resetCounter?: number
  defaultValue?: any
  currentValue?: any
  forceClear?: boolean
  upperCase?: boolean
  testId?: string
  forwardedRef?: React.RefObject<HTMLInputElement>
}

export function Input({
  onEnter,
  error,
  barcode,
  autoFocus,
  style,
  image,
  onChange,
  placeholder,
  defaultValue,
  currentValue,
  type,
  required,
  startFocus,
  label,
  disabled,
  readOnly,
  id,
  debounce,
  min,
  max,
  step,
  minLength,
  maxLength,
  showClear = true,
  customIcon,
  showLoading,
  resetCounter,
  forceClear,
  upperCase,
  testId,
  forwardedRef
}: InputProps) {
  const [started, setstarted] = useState(false)
  const [value, setvalue] = useState(defaultValue ?? '')
  const [loading, setloading] = useState(false)
  const [cleared, setcleared] = useState(false)
  const [timer, settimer] = useState<any>(null)
  const [counterClear, setCounterClear] = useState(0)
  const internalRef = useRef<HTMLInputElement>(null)
  const inputRef = forwardedRef ?? internalRef

  const fromParent = currentValue !== undefined
  const displayText = fromParent ? String(currentValue ?? '') : String(value ?? '')

  const focus = useCallback(() => {
    !readOnly && inputRef?.current?.focus()
  }, [inputRef, readOnly])

  const clear = useCallback(() => {
    if (!inputRef.current) return
    inputRef.current.value = ''
    if (!fromParent) {
      setvalue('')
    }
    onChange?.('')
    focus()
  }, [inputRef, onChange, focus, fromParent])

  useEffect(() => {
    if (currentValue === undefined) return
    const node = inputRef.current
    if (!node) return
    const next = String(currentValue ?? '')
    if (node.value !== next) {
      node.value = next
    }
  }, [currentValue, inputRef])

  useEffect(() => {
    // Gestione iniziale del focus (una sola volta)
    if (!started && (startFocus || (autoFocus && displayText === ''))) {
      setstarted(true)
      focus()
    }
    if (!barcode) {
      return
    }
    if (error && !cleared) {
      clear()
      setcleared(true)
    }
    if (!error) {
      setcleared(false)
    }
  }, [error, clear, cleared, setcleared, barcode, autoFocus, startFocus, started, focus, displayText])

  useEffect(() => {
    if (resetCounter && resetCounter > counterClear) {
      clear()
      setCounterClear(resetCounter)
    }
  }, [resetCounter, counterClear, clear])

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const newValue = upperCase ? raw.toUpperCase() : raw

    if (upperCase && raw !== newValue) {
      e.target.value = newValue
    }

    if (!fromParent) {
      setvalue(newValue)
    }
    if (debounce) {
      clearTimeout(timer)
      settimer(
        setTimeout(() => {
          onChange?.(newValue)
        }, debounce)
      )
    } else {
      onChange?.(newValue)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onEnter) {
      // Intercetta Enter prima del submit nativo del form
      if (e.preventDefault) {
        e.preventDefault()
      }
      if (e.stopPropagation) {
        e.stopPropagation()
      }
    }
  }

  const handleKeyUp = async (e) => {
    if (e.key === 'Enter' && !loading) {
      setloading(true)
      setTimeout(async () => {
        try {
          if (onEnter) {
            onEnter(inputRef.current?.value ?? '')
          }
        } catch (err) {
          // Avoid uncaught promise errors bubbling from custom handlers.
          console.error(err)
        } finally {
          setloading(false)
          setcleared(false)
          // Se il chiamante gestisce il focus tramite forwardedRef (es. FormSchemaForm),
          // non rifocalizzare forzatamente questo input.
          if (!forwardedRef) {
            focus()
          }
        }
      }, debounce || 0)
    }
  }

  const labelFocus = (e) => {
    focus()
    e.preventDefault()
  }

  const icon = image
  const valueLength = displayText.length
  const showClearIcon = showClear && !loading && !showLoading && valueLength > 0 && (forceClear || !disabled)
  return (
    <div data-testid={`input`} data-cy={label || displayText} style={style}>
      <Field className="float-label">
        {label && (
          <Label onClick={labelFocus} text={(fromParent ? currentValue : value) || defaultValue}>
            {label}
          </Label>
        )}
        <StyledInput
          label={label}
          disabled={disabled || loading}
          readOnly={readOnly ?? false}
          defaultValue={defaultValue}
          error={error}
          ref={inputRef}
          required={required}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onChange={handleChange}
          type={type}
          id={id}
          name={id}
          min={min}
          max={max}
          step={step}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete="off"
          data-testid={testId}
        />
        <ImageWrap>
          {icon && typeof icon === 'string' && valueLength === 0 && <RightImage src={icon} alt="" />}
          {icon && typeof icon !== 'string' && valueLength === 0 && <RightIcon>{icon}</RightIcon>}
          {showClearIcon && <Icons.Close style={{ marginRight: 5, cursor: 'pointer', width: 24 }} onClick={clear} />}
          {loading || (showLoading && <Icons.Loader style={{ width: 40 }} />)}
          {((valueLength === 0 && showClear) || !showClear) && !loading && !showLoading && customIcon
            ? customIcon
            : null}
        </ImageWrap>
      </Field>
      {error && <ErrorStyle>* {error}</ErrorStyle>}
    </div>
  )
}

export const StyledInput = styled.input<{ error: any; label?: React.ReactNode }>`
  flex: 1;
  height: 65px;
  background: #ffffff;
  width: 100%;
  border: ${({ error, readOnly }) => {
    if (readOnly) {
      return '0'
    }
    return error ? `2px solid ${errorColor}` : '2px solid #D2D2D2'
  }};
  cursor: ${({ readOnly }) => {
    if (readOnly) {
      return 'default'
    }
    return 'auto'
  }};
  border-radius: 10px;
  font-style: normal;
  font-weight: normal;
  color: #333333;
  font-size: 22px;
  line-height: 26px;
  padding-left: 15px;
  padding-right: 50px;
  ${({ label }) => label && `padding-top: 17px;`}
  :focus {
    box-shadow: ${({ readOnly }) => {
      if (readOnly) {
        return 'none'
      }
      return `0 0 0 3px rgba(47, 128, 237, 0.5)`
    }};
    outline: ${({ readOnly }) => {
      if (readOnly) {
        return 'none'
      }
      return `transparent auto 0`
    }};
    border-radius: 10px;
    border: ${({ readOnly, theme: t }) => {
      if (readOnly) {
        return '0'
      }
      return `2px solid ${t.accent}`
    }};
  }
  :disabled {
    opacity: 0.5;
    padding-right: 15px;
  }
`

const ImageWrap = styled.div`
  justify-content: center;
  align-items: center;
  position: absolute;
  margin: auto;
  position: absolute;
  display: flex;
  top: 0;
  bottom: 0;
  right: 10px;
`

const RightImage = styled((props) => <Image {...props} />)`
  margin: 10px 5px;
  cursor: pointer;
  min-width: 24px;
`

const RightIcon = styled.div`
  svg {
    margin-right: 5px;
    cursor: pointer;
    min-width: 24px;
  }
`

const ErrorStyle = styled.div`
  margin-left: 10px;
  margin-top: 10px;
  font-weight: bold;
  color: ${errorColor};
`

const activeField = css`
  transform: translate(0, 11px) scale(0.75) !important;
  left: 15px;
  color: ${theme.foreground};
  font-size: 16px;
  font-weight: 700;
  text-align: left;
  line-height: 21px;
`

const Field = styled.div`
  position: relative;
  margin: auto;
  flex: 1;
  display: flex;
  padding: 4px 4px;
  box-sizing: border-box;

  :focus-within label {
    ${activeField}
  }
`

const Label = styled.label<{ text?: string }>`
  font-style: normal;
  font-weight: normal;
  font-size: 22px;
  color: #666666;
  line-height: 26px;
  top: 0px;
  position: absolute;
  left: 15px;
  transform: translate(0, 20px) scale(1);
  transform-origin: top left;
  transition: all 0.2s ease-out;

  ${({ text }) => (text ? activeField : '')}
`
